/* eslint-disable react-refresh/only-export-components */
import {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
    type DependencyList,
    type ReactNode,
} from 'react';
import type { IconType } from 'react-icons';

export interface PageCrumb {
    label: string;
    to?: string;
}

export interface PageBack {
    to: string;
    label?: string;
}

export interface PageAction {
    id: string;
    label: ReactNode;
    onClick: () => void;
    icon?: IconType;
    disabled?: boolean;
    loading?: boolean;
}

export interface PageChrome {
    title?: ReactNode;
    meta?: ReactNode;
    actions?: PageAction[];
    primaryAction?: ReactNode;
    trailingCrumbs?: PageCrumb[];
    back?: PageBack;
}

const EMPTY: PageChrome = {};

const PageChromeValueContext = createContext<PageChrome>(EMPTY);
const PageChromeSetContext = createContext<(chrome: PageChrome) => void>(() => { });

/**
 * @example
 * ```tsx
 * usePageChrome(() => ({
 *   title: intl.formatMessage(messages.expenses),
 *   meta: `${home.address}, ${home.city}`,
 *   primaryAction: <Button onClick={open}>Submit expense</Button>,
 * }), [intl, home, open]);
 * ```
 */
export function usePageChrome(factory: () => PageChrome, deps: DependencyList) {
    const setChrome = useContext(PageChromeSetContext);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const chrome = useMemo(factory, deps);

    useEffect(() => {
        setChrome(chrome);
        return () => setChrome(EMPTY);
    }, [chrome, setChrome]);
}

export function PageChromeProvider({ children }: { children: ReactNode }) {
    const [chrome, setChrome] = useState<PageChrome>(EMPTY);

    return (
        <PageChromeSetContext.Provider value={setChrome}>
            <PageChromeValueContext.Provider value={chrome}>
                {children}
            </PageChromeValueContext.Provider>
        </PageChromeSetContext.Provider>
    );
}

export function usePageChromeContent(): PageChrome {
    return useContext(PageChromeValueContext);
}
